import * as React from 'react';

import { Button, ConsoleDialog, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucideMail, LucideX } from 'lucide-react';

import { notifyPriceListAtom } from '../../_graphql/mutationPriceListDocument';
import { useTranslations } from '../../i18n/useTranslations';

interface PriceListEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  priceListId: string;
  priceListType: string;
  priceListName: string;
}

export const PriceListEmailDialog = ({
  isOpen,
  onClose,
  priceListId,
  priceListType,
  priceListName,
}: PriceListEmailDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [emailInput, setEmailInput] = React.useState('');
  const [emails, setEmails] = React.useState<string[]>([]);
  const [subject, setSubject] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);

  const notifyAction = useSetAtom(notifyPriceListAtom({}));

  React.useEffect(() => {
    if (isOpen) {
      setSubject(`Price List: ${priceListName}`);
    }
  }, [isOpen, priceListName]);

  const handleClose = React.useCallback(() => {
    setEmailInput('');
    setEmails([]);
    setSubject('');
    onClose();
  }, [onClose]);

  const handleAddEmail = React.useCallback(() => {
    const trimmed = emailInput.trim();
    if (trimmed && trimmed.includes('@') && !emails.includes(trimmed)) {
      setEmails((prev) => [...prev, trimmed]);
      setEmailInput('');
    }
  }, [emailInput, emails]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        handleAddEmail();
      }
    },
    [handleAddEmail],
  );

  const handleRemoveEmail = React.useCallback((email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  }, []);

  const handleSend = React.useCallback(async () => {
    if (emails.length === 0) return;

    try {
      setIsSending(true);
      await notifyAction({
        priceListId,
        priceListType,
        to: emails,
        subject: subject || undefined,
      });
      toast.success(t.pricing.customPriceLists.toast.emailSent);
      handleClose();
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.emailError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSending(false);
    }
  }, [
    emails,
    priceListId,
    priceListType,
    subject,
    notifyAction,
    toast,
    t,
    handleClose,
  ]);

  return (
    <ConsoleDialog
      className="@PriceListEmailDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.pricing.customPriceLists.email.title}
      content={
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral/90">
              {t.pricing.customPriceLists.email.recipients}
            </label>
            <div className="flex flex-wrap gap-1">
              {emails.map((email) => (
                <span
                  key={email}
                  className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(email)}
                    className="hover:text-primary/70"
                  >
                    <LucideX size={12} />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleAddEmail}
              placeholder={t.pricing.customPriceLists.email.addRecipient}
              className="rounded-md border border-neutral/20 px-3 py-2 text-sm outline-none focus:border-primary/50"
            />
          </div>

          <TextField
            label={t.pricing.customPriceLists.email.subject}
            value={subject}
            onChange={setSubject}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSending}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideMail size={16} />}
            color="primary"
            onPress={handleSend}
            isDisabled={emails.length === 0 || isSending}
          >
            {isSending
              ? t.common.loading.saving
              : t.pricing.customPriceLists.email.sendButton}
          </Button>
        </div>
      }
    />
  );
};
